import React, { useEffect } from 'react';
import RightSlider from 'component/hoc/rightSlider/RightSlider';
import { FormikProvider, useFormik } from 'formik';
import { useWorkflowContext } from 'app-context/worflow/WorkflowContext';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { isEqual } from 'lodash';
import ApiForm from 'component/organism/workflow/forms/api-form/ApiForm';
import styles from './ApiModal.module.scss';

const ApiModal = ({ isOpen, onClose, title, id, isError }) => {
  const {
    workflowData,
    setWorkflowData,
    selectedNode,
    setIsChanged,
    initialWorkflowData,
  } = useWorkflowContext();

  const formik = useFormik({
    initialValues: {
      method: selectedNode?.api?.method || '',
      requestUrl: selectedNode?.api?.requestUrl || '',
      headers: selectedNode?.api?.headers || [],
      captureResponses: selectedNode?.api?.captureResponses || [],
      parameters: selectedNode?.api?.parameters || [],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      method: Yup.string().required('Required'),
      requestUrl: Yup.string().required('Required'),
      headers: Yup.array().of(
        Yup.object({
          key: Yup.string().required('Required'),
          value: Yup.string().required('Required'),
        }),
      ),
      captureResponses: Yup.array().of(
        Yup.object({
          key: Yup.string().required('Required'),
          value: Yup.string().required('Required'),
        }),
      ),
      parameters: Yup.array().of(
        Yup.object({
          key: Yup.string().required('Required'),
          value: Yup.string().required('Required'),
        }),
      ),
    }),
    onSubmit: (values) => {
      const initialNode = initialWorkflowData?.find((node) => node?.id === id);

      if (initialNode) {
        const initialApi = {
          method: initialNode?.api?.method || '',
          requestUrl: initialNode?.api?.requestUrl || '',
          headers: initialNode?.api?.headers || [],
          captureResponses: initialNode?.api?.captureResponses || [],
          parameters: initialNode?.api?.parameters || [],
        };

        const changed = !isEqual(values, initialApi);

        setIsChanged((prev) => {
          const alreadyExists = prev?.includes(id);

          if (changed && !alreadyExists) return [...prev, id];
          if (!changed && alreadyExists)
            return prev?.filter((nodeId) => nodeId !== id);

          return prev;
        });
      }
    },
  });

  const handleSubmit = async () => {
    try {
      const errors = await formik.validateForm();
      if (Object.keys(errors || {}).length) {
        formik.setTouched(
          {
            method: true,
            requestUrl: true,
            headers: true,
            captureResponses: true,
            parameters: true,
          },
          true,
        );
      }

      const apiValues = {
        method: formik.values.method,
        requestUrl: formik.values.requestUrl,
        headers: Array.isArray(formik.values.headers)
          ? formik.values.headers
          : [],
        captureResponses: Array.isArray(formik.values.captureResponses)
          ? formik.values.captureResponses
          : [],
        parameters: Array.isArray(formik.values.parameters)
          ? formik.values.parameters
          : [],
      };

      const nextIsError = !apiValues.method || !apiValues.requestUrl;

      setTimeout(() => {
        const updatedNodes = workflowData?.nodes?.map((node) =>
          node.id === id
            ? {
                ...node,
                api: apiValues,
                is_Error: nextIsError,
              }
            : node,
        );

        setWorkflowData((prev) => ({
          ...prev,
          nodes: updatedNodes,
        }));

        onClose();
        formik.resetForm();
      }, 100);
    } catch {
      toast.error('Something went wrong');
    }
  };

  useEffect(() => {
    if (isError) {
      formik.handleSubmit();
    }
  }, [isOpen]);

  return (
    <RightSlider
      title={title}
      action={{
        isOpen,
        setIsOpen: handleSubmit,
      }}
      contentClassName={styles['right-slider']}
      subHeaderClassName={styles['sub-header']}
      className={styles['menu-container']}
    >
      <FormikProvider value={formik}>
        <ApiForm id={id} />
      </FormikProvider>
    </RightSlider>
  );
};

export default ApiModal;
